import { Link } from "@tanstack/react-router";
import {
  User,
  Bell,
  Building2,
  CreditCard,
  Tag,
  FolderOpen,
  Sparkles,
  Cpu,
  BrainCircuit,
  ClipboardCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation, cn } from "@/packages/utils";
import { useWorkspace } from "@/routes/$workspaceSlug/route";
import { useWorkspaceSectionNav } from "./use-workspace-section-nav";
import { WORKSPACE_SETTINGS_SECTIONS } from "./workspace-settings-sections";

interface SettingsNavItem {
  icon: LucideIcon;
  labelKey: string;
  /** Relative path after /${workspaceSlug} prefix */
  subPath: string;
}

const accountNav: SettingsNavItem[] = [
  { icon: User, labelKey: "nav.profile", subPath: "/settings/profile" },
  {
    icon: Bell,
    labelKey: "nav.notifications",
    subPath: "/settings/notifications",
  },
  // { icon: Shield, labelKey: "nav.security", subPath: "/settings/security" },
];

const orgNav: SettingsNavItem[] = [
  {
    icon: Building2,
    labelKey: "nav.workspace",
    subPath: "/settings/workspace",
  },
  { icon: Tag, labelKey: "nav.documentTags", subPath: "/settings/tags" },
  { icon: Tag, labelKey: "nav.auditTags", subPath: "/settings/audit-tags" },
  {
    icon: FolderOpen,
    labelKey: "nav.categories",
    subPath: "/settings/categories",
  },
  {
    icon: ClipboardCheck,
    labelKey: "nav.auditConfig",
    subPath: "/settings/audit-config",
  },
  {
    icon: Sparkles,
    labelKey: "nav.aiPreferences",
    subPath: "/settings/ai-rules",
  },
  {
    icon: BrainCircuit,
    labelKey: "AI Provider",
    subPath: "/settings/ai-provider",
  },
  {
    icon: CreditCard,
    labelKey: "nav.billing",
    subPath: "/settings/billing",
  },
  {
    icon: Cpu,
    labelKey: "nav.aiUsage",
    subPath: "/settings/ai-usage",
  },
  // {
  //   icon: Puzzle,
  //   labelKey: "nav.integrations",
  //   subPath: "/settings/integrations",
  // },
];

function SettingsNavLink({
  icon: Icon,
  label,
  to,
}: {
  icon: LucideIcon;
  label: string;
  to: string;
}) {
  const base =
    "group relative flex w-full items-center gap-3 rounded-[14px] px-3.5 text-[13px] transition-all duration-150";

  const activeClass = `${base} border border-accent/20 bg-[linear-gradient(180deg,var(--color-accent-soft),rgba(255,255,255,0.88))] text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_14px_30px_-24px_var(--color-accent)]`;

  const inactiveClass = `${base} border border-transparent text-secondary hover:border-white/70 hover:bg-white/70 hover:text-primary`;

  return (
    <Link
      to={to}
      params={(prev) => prev}
      search={(prev) => prev}
      activeProps={{ className: activeClass }}
      inactiveProps={{ className: inactiveClass }}
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-white/75 text-secondary transition-colors group-hover:text-primary">
        <Icon size={16} strokeWidth={1.75} className="shrink-0" />
      </div>
      <span>{label}</span>
    </Link>
  );
}

function WorkspaceSubNav({ isOwner }: { isOwner: boolean }) {
  const { t } = useTranslation(["settings"]);
  const { activeSectionId, scrollToSection, isWorkspacePage } =
    useWorkspaceSectionNav(isOwner);

  if (!isWorkspacePage) return null;

  const sections = WORKSPACE_SETTINGS_SECTIONS.filter(
    (section) => !section.ownerOnly || isOwner,
  );

  return (
    <div className="ml-[22px] mt-1 space-y-0.5 border-l border-border/70 pl-3">
      {sections.map((section) => {
        const isActive = activeSectionId === section.id;

        return (
          <button
            key={section.id}
            type="button"
            onClick={() => scrollToSection(section.id)}
            className={cn(
              "block w-full rounded-[10px] px-2.5 py-1.5 text-left text-[12px] leading-5 transition-colors",
              isActive
                ? "bg-accent/8 font-medium text-accent"
                : "text-secondary hover:bg-white/70 hover:text-primary",
            )}
          >
            {t(section.labelKey as "settings:workspace.sections.general")}
          </button>
        );
      })}
    </div>
  );
}

export function SettingsSidebar({ workspaceSlug }: { workspaceSlug: string }) {
  const { t } = useTranslation(["settings"]);
  const { workspace } = useWorkspace();
  const isOwner = workspace.role === "owner";
  const prefix = `/${workspaceSlug}`;

  return (
    <nav className="shrink-0 lg:sticky lg:top-6 lg:w-[260px] lg:self-start">
      <div className="overflow-hidden rounded-[24px] border border-white/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.88),rgba(255,255,255,0.78))] p-3 shadow-[0_20px_50px_-38px_rgba(15,23,42,0.4)] backdrop-blur-md lg:flex lg:max-h-[calc(100vh-1.5rem)] lg:min-h-0 lg:flex-col">
        {/* <div className="mb-4 rounded-[18px] border border-white/70 bg-white/70 px-4 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">
            {t("settings:sidebar.eyebrow")}
          </p>
          <h1 className="mt-2 text-[22px] font-semibold tracking-[-0.03em] text-primary">
            {t("settings:title")}
          </h1>
          <p className="mt-1 text-[13px] leading-5 text-secondary">
            {t("settings:sidebar.description")}
          </p>
        </div> */}

        <div className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:pr-1">
          <div className="mb-5">
            <h3 className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">
              {t("settings:sections.account")}
            </h3>
            <div className="space-y-1">
              {accountNav.map((item) => (
                <SettingsNavLink
                  key={item.subPath}
                  icon={item.icon}
                  label={t(item.labelKey as "settings:nav.profile") as string}
                  to={`${prefix}${item.subPath}`}
                />
              ))}
            </div>
          </div>

          <div>
            <h3 className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">
              {t("settings:sections.organization")}
            </h3>
            <div className="space-y-1">
              {orgNav.map((item) =>
                item.subPath === "/settings/workspace" ? (
                  <div key={item.subPath}>
                    <SettingsNavLink
                      icon={item.icon}
                      label={
                        t(item.labelKey as "settings:nav.profile") as string
                      }
                      to={`${prefix}${item.subPath}`}
                    />
                    <WorkspaceSubNav isOwner={isOwner} />
                  </div>
                ) : (
                  <SettingsNavLink
                    key={item.subPath}
                    icon={item.icon}
                    label={
                      t(item.labelKey as "settings:nav.profile") as string
                    }
                    to={`${prefix}${item.subPath}`}
                  />
                ),
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
